// Shared connection settings constants and helpers

export const SYNC_FREQUENCY_OPTIONS = [
  { value: '1', label: '1 min' },
  { value: '5', label: '5 min' },
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '180', label: '3 hours' },
  { value: '360', label: '6 hours' },
  { value: '480', label: '8 hours' },
  { value: '720', label: '12 hours' },
  { value: '1440', label: '24 hours' },
];

const roundTo30 = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const rounded = m < 15 ? 0 : m < 45 ? 30 : 0;
  const finalH = rounded === 0 && m >= 45 ? (h + 1) % 24 : h;
  return `${String(finalH).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`;
};

export const pacificToEst = (pacificTime: string): string => {
  const [hours, minutes] = pacificTime.split(':').map(Number);
  const base = new Date();
  base.setUTCHours(hours + 8, minutes, 0, 0);
  const getPacific = (d: Date) => d.toLocaleTimeString('en-US', {
    timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  for (let i = 0; i < 3; i++) {
    const [pH] = getPacific(base).split(':').map(Number);
    const diff = hours - pH;
    if (diff === 0) break;
    base.setUTCHours(base.getUTCHours() + diff);
  }
  const raw = base.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return roundTo30(raw);
};

export const estToPacific = (estTime: string): string => {
  const [hours, minutes] = estTime.split(':').map(Number);
  const base = new Date();
  base.setUTCHours(hours + 5, minutes, 0, 0);
  const getEastern = (d: Date) => d.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  for (let i = 0; i < 3; i++) {
    const [eH] = getEastern(base).split(':').map(Number);
    const diff = hours - eH;
    if (diff === 0) break;
    base.setUTCHours(base.getUTCHours() + diff);
  }
  const raw = base.toLocaleTimeString('en-US', {
    timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return roundTo30(raw);
};

export interface SchemaTable {
  name_in_source: string;
  enabled: boolean;
  sync_mode?: string;
  columns?: Record<string, { enabled: boolean; hashed?: boolean }>;
}

export interface SchemaEntry {
  name_in_source: string;
  enabled: boolean;
  tables: Record<string, SchemaTable>;
}

export const toggleSchemaEnabled = (
  prev: Record<string, SchemaEntry>,
  schemaName: string
): Record<string, SchemaEntry> => ({
  ...prev,
  [schemaName]: {
    ...prev[schemaName],
    enabled: !prev[schemaName]?.enabled,
  },
});

export const toggleTableEnabled = (
  prev: Record<string, SchemaEntry>,
  schemaName: string,
  tableName: string
): Record<string, SchemaEntry> => ({
  ...prev,
  [schemaName]: {
    ...prev[schemaName],
    tables: {
      ...prev[schemaName]?.tables,
      [tableName]: {
        ...prev[schemaName]?.tables?.[tableName],
        enabled: !prev[schemaName]?.tables?.[tableName]?.enabled,
      },
    },
  },
});

export function computeAllowedSchemaChanges(
  schemas: Record<string, SchemaEntry>,
  tablesToSync: string[]
): Record<string, SchemaEntry> {
  const allowSet = new Set(tablesToSync);
  const result: Record<string, SchemaEntry> = {};
  for (const [schemaName, schema] of Object.entries(schemas)) {
    result[schemaName] = {
      ...schema,
      enabled: true,
      tables: Object.fromEntries(
        Object.entries(schema.tables || {}).map(([tableName, table]) => [
          tableName,
          { ...table, enabled: allowSet.has(tableName) },
        ])
      ),
    };
  }
  return result;
}

// Backward-compatible alias; callers should migrate to computeAllowedSchemaChanges.
export const computeDefaultSchemaChanges = computeAllowedSchemaChanges;

export function extractTableLists(
  schemaChanges: Record<string, SchemaEntry>
): { allowedTables: string[]; disabledTables: string[] } {
  const allowedTables: string[] = [];
  const disabledTables: string[] = [];
  for (const [schemaName, schema] of Object.entries(schemaChanges)) {
    for (const [tableName, table] of Object.entries(schema.tables || {})) {
      const key = `${schemaName}.${tableName}`;
      if (table.enabled) allowedTables.push(key);
      else disabledTables.push(key);
    }
  }
  return { allowedTables, disabledTables };
}

export function computeNewTables(
  schemaChanges: Record<string, SchemaEntry>,
  allowedTables: string[],
  disabledTables: string[]
): Array<{ schemaName: string; tableName: string }> {
  const known = new Set([...allowedTables, ...disabledTables]);
  return Object.entries(schemaChanges).flatMap(([schemaName, schema]) =>
    Object.keys(schema.tables || {})
      .filter(tableName => !known.has(`${schemaName}.${tableName}`))
      .map(tableName => ({ schemaName, tableName }))
  );
}

/**
 * Mirrors `CONNECTION_UNAVAILABLE_ERROR_CODE` in
 * `supabase/functions/_shared/fivetranConnectionAvailability.ts`. Duplicated
 * rather than imported because a Vite/React module must not depend on an edge
 * function's source tree; a contract test asserts the two stay identical.
 */
export const CONNECTION_UNAVAILABLE_ERROR_CODE = 'connection/unavailable-in-environment';

/** True when the body is the structured unavailable-in-this-environment envelope (UC-168). */
export function isConnectionUnavailableBody(rawBody: string): boolean {
  try {
    const parsed = JSON.parse(rawBody);
    return parsed?.error?.code === CONNECTION_UNAVAILABLE_ERROR_CODE;
  } catch {
    return false;
  }
}

/**
 * Message to surface for a failed connection-settings response. Structured
 * envelopes yield their human-readable message; anything else falls back to the
 * raw body verbatim, so existing error reporting is unchanged.
 */
export function extractConnectionErrorMessage(rawBody: string): string {
  try {
    const parsed = JSON.parse(rawBody);
    const message = parsed?.error?.message;
    if (parsed?.error?.code === CONNECTION_UNAVAILABLE_ERROR_CODE && typeof message === 'string' && message.length > 0) {
      return message;
    }
  } catch {
    // Not JSON — fall through to the raw body.
  }
  return rawBody;
}
